/**
 * Brand Color Palette for Granny AI Application
 *
 * This file defines the official color scheme for the application.
 * All components should reference these colors to maintain consistency.
 *
 * Primary Theme: Purple/Plum
 */

export const BRAND_COLORS = {
  // Primary Brand Colors
  primary: {
    main: "#473243", // Dark purple - Main brand color for text, buttons, and primary elements
    light: "#766087", // Medium purple - For secondary text and subtle elements
    lighter: "#9d8aad", // Light purple - For hover states and accents
    lightest: "#d8cdde", // Very light purple - For backgrounds (sidebar, cards)
    pale: "#f5f2f4", // Pale purple - For subtle backgrounds and hover states
  },

  // Text Colors
  text: {
    primary: "#473243", // Main text color (headings, body text, labels)
    secondary: "#766087", // Secondary text (descriptions, captions)
    muted: "#9d8aad", // Muted text (placeholders, disabled states)
    inverse: "#ffffff", // White text (for dark backgrounds)
  },

  // Background Colors
  background: {
    primary: "#ffffff", // Main page background
    secondary: "#f5f2f4", // Secondary background (cards, sections)
    sidebar: "#d8cdde", // Sidebar background
    hover: "rgba(71, 50, 67, 0.08)", // Hover state background
    active: "rgba(71, 50, 67, 0.15)", // Active state background
  },

  // Button Colors
  button: {
    primary: "#473243", // Primary button background
    primaryHover: "#5a3f54", // Primary button hover state
    primaryText: "#ffffff", // Primary button text
    outline: "#473243", // Outline button border and text
    outlineHover: "#f5f2f4", // Outline button hover background
    ghost: "#473243", // Ghost button text
    ghostHover: "#f5f2f4", // Ghost button hover background
  },

  // Border Colors
  border: {
    default: "rgba(71, 50, 67, 0.2)", // Default border color
    light: "rgba(71, 50, 67, 0.1)", // Light border
    focus: "#473243", // Focus ring color
  },

  // Status Colors
  status: {
    success: "#10b981", // Success messages
    error: "#ef4444", // Error messages, required field indicators
    warning: "#f59e0b", // Warning messages
    info: "#3b82f6", // Info messages
  },

  // Semantic Colors (for specific use cases)
  semantic: {
    required: "#ef4444", // Required field asterisk (*)
    disabled: "rgba(71, 50, 67, 0.4)", // Disabled state
    placeholder: "#9d8aad", // Input placeholder text
  },
} as const;

/**
 * Color Usage Guidelines:
 *
 * 1. HEADINGS: Use BRAND_COLORS.text.primary (#473243)
 * 2. BODY TEXT: Use BRAND_COLORS.text.primary (#473243)
 * 3. SECONDARY TEXT: Use BRAND_COLORS.text.secondary (#766087)
 * 4. BUTTONS: Use BRAND_COLORS.button.primary (#473243)
 * 5. SIDEBAR: Use BRAND_COLORS.background.sidebar (#d8cdde)
 * 6. HOVER STATES: Use BRAND_COLORS.background.hover
 * 7. BORDERS: Use BRAND_COLORS.border.default
 *
 * Example Usage:
 * ```tsx
 * import { BRAND_COLORS } from '@/constants/colors';
 *
 * <h1 style={{ color: BRAND_COLORS.text.primary }}>Heading</h1>
 * <button style={{ backgroundColor: BRAND_COLORS.button.primary }}>Click</button>
 * <div style={{ backgroundColor: BRAND_COLORS.background.sidebar }}>Sidebar</div>
 * ```
 */

// Export individual color values for convenience
export const PRIMARY_COLOR = BRAND_COLORS.primary.main;
export const SIDEBAR_BG = BRAND_COLORS.background.sidebar;
export const TEXT_COLOR = BRAND_COLORS.text.primary;
export const BUTTON_COLOR = BRAND_COLORS.button.primary;

// Type for color keys (useful for TypeScript)
export type BrandColorKey = keyof typeof BRAND_COLORS;
export type PrimaryColorKey = keyof typeof BRAND_COLORS.primary;
export type TextColorKey = keyof typeof BRAND_COLORS.text;
export type BackgroundColorKey = keyof typeof BRAND_COLORS.background;
export type ButtonColorKey = keyof typeof BRAND_COLORS.button;
